import { type FormEvent, useEffect, useState } from 'react';
import type { MarketingEvent } from '@bcc/shared';
import { SITE_FILE_CONTROL, SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { apiUrl } from '../../lib/apiUrl';

type RsvpRow = {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  ticket_code: string | null;
  created_at: string;
  attended_at?: string | null;
};

export function AdminEvents() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [events, setEvents] = useState<MarketingEvent[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [crmEventId, setCrmEventId] = useState<string | null>(null);
  const [checkInCode, setCheckInCode] = useState('');
  const [checkInRsvpId, setCheckInRsvpId] = useState('');
  const [checkInMsg, setCheckInMsg] = useState<string | null>(null);
  const [rsvps, setRsvps] = useState<RsvpRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [timeLabel, setTimeLabel] = useState('9:30 am – 11:30 am');
  const [venue, setVenue] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [eventUrl, setEventUrl] = useState('');
  const [published, setPublished] = useState(true);
  const [file, setFile] = useState<File | null>(null);

  async function refresh() {
    if (!token) return;
    const r = await apiFetch<{ events: MarketingEvent[] }>('/api/staff/marketing/events', { token });
    setEvents(r.events);
  }

  useEffect(() => {
    void refresh();
  }, [token]);

  async function loadRsvps(id: string) {
    if (!token) return;
    const r = await apiFetch<{ rsvps: RsvpRow[] }>(`/api/staff/marketing/events/${id}/rsvps`, { token });
    setRsvps(r.rsvps);
  }

  async function submitCheckIn(eventId: string) {
    if (!token) return;
    setCheckInMsg(null);
    try {
      const body: Record<string, string> = {};
      if (checkInCode.trim()) body.ticket_code = checkInCode.trim();
      else if (checkInRsvpId.trim()) body.rsvp_id = checkInRsvpId.trim();
      else {
        setCheckInMsg('Enter ticket code or RSVP id');
        return;
      }
      const res = await apiFetch<{ ok: boolean; already: boolean; rsvp: RsvpRow }>(
        `/api/staff/marketing/events/${eventId}/check-in`,
        { method: 'POST', token, body: JSON.stringify(body) }
      );
      setCheckInMsg(res.already ? 'Already checked in' : 'Checked in ✓');
      setCheckInCode('');
      setCheckInRsvpId('');
      await loadRsvps(eventId);
    } catch (e) {
      setCheckInMsg((e as Error).message);
    }
  }

  async function openCrm(id: string) {
    setCrmEventId(id);
    setCheckInMsg(null);
    await loadRsvps(id);
  }

  async function toggleExpand(id: string) {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    await loadRsvps(id);
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setErr(null);
    try {
      let imagePath: string | null = null;
      if (file) {
        const ext = file.name.split('.').pop() || 'jpg';
        const path = `events/${crypto.randomUUID()}.${ext}`;
        const up = await supabase.storage.from('event-assets').upload(path, file, { contentType: file.type || 'image/jpeg' });
        if (up.error) throw new Error(up.error.message);
        imagePath = path;
      }
      const startsIso = new Date(startsAt).toISOString();
      const endsIso = endsAt ? new Date(endsAt).toISOString() : null;
      await apiFetch('/api/staff/marketing/events', {
        method: 'POST',
        token,
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          starts_at: startsIso,
          ends_at: endsIso,
          time_label: timeLabel.trim() || null,
          venue: venue.trim(),
          is_online: isOnline,
          event_url: isOnline ? (eventUrl.trim() || null) : null,
          image_path: imagePath,
          published,
          timezone: 'America/New_York',
        }),
      });
      setTitle('');
      setDescription('');
      setStartsAt('');
      setEndsAt('');
      setFile(null);
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function remove(id: string) {
    if (!token) return;
    if (!window.confirm('Delete this event and all RSVPs?')) return;
    await apiFetch(`/api/staff/marketing/events/${id}`, { method: 'DELETE', token });
    await refresh();
  }

  return (
    <div className="max-w-4xl space-y-10">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--home-text-on-dark)]">Upload &amp; manage events</h1>
        <p className="mt-1 text-sm text-[var(--home-muted-on-dark)]">
          Published events appear on the public Events page. RSVPs receive a confirmation email (configure Resend in the API env).
        </p>
      </div>

      <form className={`space-y-4 p-6 ${SITE_GLASS_CARD}`} onSubmit={onCreate}>
        <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">New event</h2>
        {err ? <p className="text-sm text-rose-300">{err}</p> : null}
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Title</span>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Description</span>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Starts (local)</span>
            <input
              required
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Ends (optional)</span>
            <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Time label (display)</span>
          <input value={timeLabel} onChange={(e) => setTimeLabel(e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Event format</span>
          <select
            value={isOnline ? 'online' : 'in_person'}
            onChange={(e) => setIsOnline(e.target.value === 'online')}
            className={`w-full ${SITE_FORM_CONTROL}`}
          >
            <option value="in_person">In person</option>
            <option value="online">Online</option>
          </select>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Venue {isOnline ? '(optional for virtual)' : ''}</span>
          <input
            required={!isOnline}
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            className={`w-full ${SITE_FORM_CONTROL}`}
            placeholder={isOnline ? 'e.g. Zoom · link below' : ''}
          />
        </label>
        {isOnline ? (
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Online event link</span>
            <input value={eventUrl} onChange={(e) => setEventUrl(e.target.value)} type="url" className={`w-full ${SITE_FORM_CONTROL}`} placeholder="https://…" />
          </label>
        ) : null}
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Hero image (optional)</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className={`w-full text-sm text-[var(--home-muted-on-dark)] ${SITE_FILE_CONTROL}`}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--home-text-on-dark)]">
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
          Published
        </label>
        <button type="submit" className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] hover:brightness-105">
          Create event
        </button>
      </form>

      <section className={`${SITE_GLASS_CARD} overflow-hidden`}>
        <header className="border-b border-white/[0.08] px-5 py-3 text-sm font-semibold text-[var(--home-text-on-dark)]">
          Events &amp; RSVPs
        </header>
        <ul className="divide-y divide-white/[0.08]">
          {events.map((ev) => (
            <li key={ev.id} className="px-5 py-4 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-[var(--home-text-on-dark)]">{ev.title}</p>
                  <p className="text-xs text-[var(--home-muted-on-dark)]">
                    {ev.is_online ? 'Online' : 'In person'}
                    {' · '}
                    {new Date(ev.starts_at).toLocaleString()} · {ev.rsvp_count ?? 0} RSVP
                    {(ev.rsvp_count ?? 0) === 1 ? '' : 's'}
                    {typeof ev.attendance_count === 'number' ? ` · ${ev.attendance_count} checked in` : null}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void openCrm(ev.id)}
                    className="rounded-lg bg-[var(--home-miami-blue)]/25 px-3 py-1 text-xs text-[var(--home-text-on-dark)] hover:bg-[var(--home-miami-blue)]/35"
                  >
                    Ticket CRM
                  </button>
                  <button
                    type="button"
                    onClick={() => void toggleExpand(ev.id)}
                    className="rounded-lg bg-white/10 px-3 py-1 text-xs text-[var(--home-text-on-dark)] hover:bg-white/15"
                  >
                    {expanded === ev.id ? 'Hide RSVPs' : 'View RSVPs'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void remove(ev.id)}
                    className="rounded-lg border border-rose-500/40 px-3 py-1 text-xs text-rose-300"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {expanded === ev.id ? (
                <div className="mt-3 overflow-x-auto rounded-lg border border-white/[0.08]">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-white/[0.06] text-[var(--home-muted-on-dark)]">
                      <tr>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Ticket</th>
                        <th className="px-3 py-2">Attended</th>
                        <th className="px-3 py-2">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rsvps.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-[var(--home-muted-on-dark)]">
                            No RSVPs yet.
                          </td>
                        </tr>
                      ) : (
                        rsvps.map((r) => (
                          <tr key={r.id} className="border-t border-white/[0.06]">
                            <td className="px-3 py-2 text-[var(--home-text-on-dark)]">{r.full_name}</td>
                            <td className="px-3 py-2">{r.email}</td>
                            <td className="px-3 py-2">{r.ticket_code ?? '—'}</td>
                            <td className="px-3 py-2 text-[var(--home-muted-on-dark)]">
                              {r.attended_at ? new Date(r.attended_at).toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2 text-[var(--home-muted-on-dark)]">{new Date(r.created_at).toLocaleString()}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {events.length === 0 ? <p className="px-5 py-6 text-sm text-[var(--home-muted-on-dark)]">No events yet.</p> : null}
      </section>

      <p className="text-xs text-[var(--home-muted-on-dark)]">
        Public RSVP endpoint {apiUrl('/api/public/events/:eventId/rsvp')}
      </p>

      {crmEventId ? (
        <div className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center">
          <button type="button" className="absolute inset-0 bg-black/75" aria-label="Close" onClick={() => setCrmEventId(null)} />
          <div className={`relative z-10 m-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto p-6 ${SITE_GLASS_CARD}`}>
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold text-[var(--home-text-on-dark)]">Ticket CRM</h2>
              <button type="button" onClick={() => setCrmEventId(null)} className="text-sm text-[var(--home-muted-on-dark)] hover:text-white">
                Close
              </button>
            </div>
            <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
              {events.find((e) => e.id === crmEventId)?.title}
            </p>
            <div className="mt-4 space-y-3 rounded-xl border border-white/10 bg-black/20 p-4">
              <p className="font-display text-[0.6rem] font-bold uppercase tracking-[0.14em] text-[var(--home-miami-blue)]">
                Check-in (scanner / manual)
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="block flex-1 text-xs">
                  Ticket code
                  <input
                    value={checkInCode}
                    onChange={(e) => setCheckInCode(e.target.value)}
                    placeholder="BRK-…"
                    className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                  />
                </label>
                <label className="block flex-1 text-xs">
                  Or RSVP id (UUID)
                  <input
                    value={checkInRsvpId}
                    onChange={(e) => setCheckInRsvpId(e.target.value)}
                    className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void submitCheckIn(crmEventId)}
                  className="rounded-lg bg-emerald-600/90 px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
                >
                  Confirm attendance
                </button>
              </div>
              <p className="text-xs text-[var(--home-muted-on-dark)]">
                Paste code from QR JSON or scan into the ticket field. For camera scanning, use your device’s keyboard scanner or a USB wedge — same as manual entry.
              </p>
              {checkInMsg ? <p className="text-sm text-emerald-200/90">{checkInMsg}</p> : null}
            </div>
            <div className="mt-4 overflow-x-auto rounded-lg border border-white/[0.08]">
              <table className="w-full text-left text-xs">
                <thead className="bg-white/[0.06] text-[var(--home-muted-on-dark)]">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Ticket</th>
                    <th className="px-3 py-2">Attended</th>
                  </tr>
                </thead>
                <tbody>
                  {rsvps.map((r) => (
                    <tr key={r.id} className="border-t border-white/[0.06]">
                      <td className="px-3 py-2 text-[var(--home-text-on-dark)]">{r.full_name}</td>
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">{r.ticket_code ?? '—'}</td>
                      <td className="px-3 py-2">{r.attended_at ? '✓' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
